import { AppLogo } from '@/components/common/AppLogo';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/date';
import { formatTaka } from '@/utils/number';
import {
  PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE,
  PCMP_PAYMENT_TYPE_LABELS,
  PCMP_REPORT_COMPANY,
} from '../../constants/pcmpProposal.constants';
import type { PcmpLaneCell, PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { PcmpReportFields, PcmpReportSection, type PcmpReportField } from './PcmpReportSection';
import { PcmpReportStudentTable } from './PcmpReportStudentTable';

interface PcmpProposalReportProps {
  proposal: PcmpProposalDetail;
  printedAt: Date;
}

const formatPercent = (value: number): string => `${value.toFixed(2)}%`;

const join = (...parts: (string | null)[]): string =>
  parts.map((part) => part ?? EMPTY_VALUE).join(' / ');

const laneText = ({ text, sub }: PcmpLaneCell): string | null =>
  text ? `${text}${sub ? ` (${sub})` : ''}` : null;

function calculationFields({ calculation }: PcmpProposalDetail): PcmpReportField[] {
  const fields: PcmpReportField[] = [
    { label: 'Total MP Amount', value: formatTaka(calculation.agreed) },
    { label: 'Disbursed Till Now', value: formatTaka(calculation.disbursed) },
    { label: 'Proposed', value: formatTaka(calculation.inFlight) },
    { label: 'Due', value: formatTaka(calculation.due) },
    { label: 'Total CMP %', value: formatPercent(calculation.totalCmpPercent) },
    { label: 'Per Student Cost', value: formatTaka(calculation.perStudentCost) },
  ];
  if (calculation.extra > 0) {
    fields.push({ label: 'Overpaid', value: formatTaka(calculation.extra), isDanger: true });
  }
  if (calculation.categorySplit) {
    fields.push(
      { label: 'Grammar %', value: formatPercent(calculation.categorySplit.grammarPercent) },
      { label: 'Practice %', value: formatPercent(calculation.categorySplit.practicePercent) },
    );
  }
  return fields;
}

function teacherFields({ teacher, payment }: PcmpProposalDetail): PcmpReportField[] {
  const { balance } = teacher;
  const fields: PcmpReportField[] = [
    { label: 'Teacher', value: teacher.name },
    { label: 'Mobile', value: teacher.mobile },
    { label: 'Designation', value: teacher.designation },
    { label: 'Subject', value: teacher.subject },
    { label: 'Agreed / Total MP', value: formatTaka(teacher.agreedAmount) },
    { label: 'Disbursed Till Now', value: formatTaka(balance?.disbursed) },
    { label: 'Proposed (This)', value: formatTaka(payment.proposedAmount) },
    { label: 'Due', value: formatTaka(balance?.due) },
  ];
  if (balance && balance.extra > 0) {
    fields.push({ label: 'Overpaid', value: formatTaka(balance.extra), isDanger: true });
  }
  return fields;
}

function paymentFields({
  payment,
  proposalType,
  challanNo,
}: PcmpProposalDetail): PcmpReportField[] {
  const { account } = payment;
  const kind = account?.kind ?? PCMP_ACCOUNT_KIND_BY_PAYMENT_TYPE[payment.paymentType];
  const onBehalf = payment.isOnBehalf ? (payment.onBehalfName ?? 'Yes') : 'Own account';
  const fields: PcmpReportField[] = [
    {
      label: 'Method',
      value: `${PCMP_PAYMENT_TYPE_LABELS[payment.paymentType]}${kind === 'MFS' ? ' (MFS)' : ''}`,
    },
    { label: 'A/C Holder Name', value: kind ? account?.holderName : null },
  ];
  if (kind === 'BANK') {
    fields.push(
      {
        label: 'Bank / Branch',
        value: join(account?.bankName ?? null, account?.branchName ?? null),
      },
      { label: 'A/C No', value: account?.accountNo },
      { label: 'Routing', value: account?.routingNo },
    );
  } else if (kind === 'MFS') {
    fields.push({ label: 'Mobile No', value: account?.mobileNo });
  } else {
    fields.push({
      label: 'Payment',
      value: `${PCMP_PAYMENT_TYPE_LABELS[payment.paymentType]} (no account)`,
    });
  }
  fields.push(
    { label: 'On Behalf', value: onBehalf },
    {
      label: 'Payment Date',
      value: payment.paymentDate ? formatDisplayDate(payment.paymentDate) : null,
    },
    { label: 'Reference No', value: payment.referenceNo },
    { label: 'Proposal Type', value: proposalType },
    { label: 'Challan No', value: challanNo },
  );
  return fields;
}

/** Who placed the current hold: the latest HELD event's user. */
function heldByName({ timeline }: PcmpProposalDetail): string | null {
  for (const card of [...timeline.activity].reverse()) {
    if (card.entries.some((entry) => entry.eventType === 'HELD')) return card.userName;
  }
  return null;
}

function approvalFields(proposal: PcmpProposalDetail): PcmpReportField[] {
  const { lanes } = proposal.status;
  const fields: PcmpReportField[] = [
    { label: 'Marketing', value: laneText(lanes.marketing) },
    { label: 'MIS', value: laneText(lanes.mis) },
    { label: 'Accounts', value: laneText(lanes.accounts) },
    { label: 'Overall', value: laneText(lanes.overall) ?? proposal.status.status.label },
  ];
  // A hold parks the proposal without moving any lane, so it gets its own line.
  if (proposal.hold) {
    const heldBy = heldByName(proposal);
    fields.push(
      { label: 'Held At', value: proposal.hold.stageLabel, isDanger: true },
      {
        label: 'Held By',
        value: `${heldBy ?? EMPTY_VALUE} (${formatDisplayDate(proposal.hold.heldAt)})`,
      },
    );
  }
  return fields;
}

/** The one-page proposal report (legacy ProposalReport.cshtml). */
export function PcmpProposalReport({ proposal, printedAt }: PcmpProposalReportProps) {
  const { mo, institute, teacher } = proposal;
  const promotionKind = proposal.proposalType === 'BMP' ? 'Book' : 'Cash';

  return (
    <div className="flex flex-col gap-3 text-neutral-900">
      <header className="flex items-center gap-3 border-b-2 border-neutral-800 pb-2">
        <AppLogo className="size-12" />
        <div className="flex flex-col">
          <span className="text-lg leading-tight font-bold">{PCMP_REPORT_COMPANY.name}</span>
          <span className="text-[11px] text-neutral-600">{PCMP_REPORT_COMPANY.address}</span>
        </div>
      </header>

      <h1 className="text-center text-sm font-bold tracking-wide uppercase">
        Proposed {promotionKind} Market Promotion — Proposal Report
      </h1>

      <div className="grid grid-cols-3 rounded-sm border border-neutral-400 text-[11px]">
        {[
          { label: 'Reference', value: proposal.refNo },
          { label: 'Total Amount', value: formatTaka(proposal.payment.proposedAmount) },
          { label: 'Date', value: formatDisplayDate(proposal.proposalDate) },
        ].map((meta) => (
          <div
            key={meta.label}
            className="flex flex-col border-r border-neutral-400 px-2 py-1 last:border-r-0"
          >
            <span className="text-[9px] tracking-wide text-neutral-600 uppercase">
              {meta.label}
            </span>
            <span className="font-bold">{meta.value}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PcmpReportSection title="Marketing Officer Info">
          <PcmpReportFields
            pairsPerRow={1}
            fields={[
              { label: 'Name', value: mo.fullName },
              { label: 'HR / ID', value: mo.hrEmpId },
              { label: 'Mobile', value: mo.mobile },
              { label: 'Designation', value: mo.designationName },
              { label: 'Thana', value: mo.thanaName },
              { label: 'District / Division', value: join(mo.districtName, mo.pcmpDivision) },
            ]}
          />
        </PcmpReportSection>
        <PcmpReportSection title="Institute Info">
          <PcmpReportFields
            pairsPerRow={1}
            fields={[
              { label: 'Name', value: institute.name },
              { label: 'Lectrack Code', value: institute.id },
              { label: 'EIIN', value: institute.eiinNumber },
              { label: 'Project', value: proposal.project.displayName },
              {
                label: 'Thana / District',
                value: join(institute.thanaName, institute.districtName),
              },
            ]}
          />
        </PcmpReportSection>
      </div>

      <PcmpReportSection title="Institute Calculation">
        <PcmpReportFields fields={calculationFields(proposal)} />
      </PcmpReportSection>

      <PcmpReportSection title={`Teacher & ${promotionKind} Promotion Info`}>
        <PcmpReportFields fields={teacherFields(proposal)} />
        {teacher.categoryAmounts.length > 0 ? (
          <table className="w-full border-collapse text-[10px]">
            <thead className="bg-neutral-100">
              <tr>
                <th className="border border-neutral-300 px-1.5 py-0.5 text-left">Category</th>
                <th className="border border-neutral-300 px-1.5 py-0.5 text-right">
                  Agreed Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {teacher.categoryAmounts.map((item) => (
                <tr key={item.categoryId}>
                  <td className="border border-neutral-300 px-1.5 py-0.5">{item.categoryName}</td>
                  <td className="border border-neutral-300 px-1.5 py-0.5 text-right tabular-nums">
                    {formatTaka(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </PcmpReportSection>

      <PcmpReportSection title="Payment Info">
        <PcmpReportFields fields={paymentFields(proposal)} />
      </PcmpReportSection>

      {proposal.students.length > 0 ? (
        <PcmpReportSection title="Student Summary (Category Wise)">
          <PcmpReportStudentTable students={proposal.students} calculation={proposal.calculation} />
        </PcmpReportSection>
      ) : null}

      <PcmpReportSection title="Approval Status">
        <PcmpReportFields fields={approvalFields(proposal)} />
      </PcmpReportSection>

      <footer className="flex flex-col gap-1 border-t border-neutral-400 pt-2 text-[10px]">
        <p className="text-center font-semibold tracking-wide uppercase">
          This is a computer generated document and it does not require any signature
        </p>
        <div className="flex justify-between gap-4">
          <span>
            Date: <strong>{formatDisplayDateTime(printedAt)}</strong>
            <br />
            Powered by <strong>Innospace Infotech Ltd.</strong>
          </span>
          <span className="text-right">
            Prepared for <strong>{PCMP_REPORT_COMPANY.name}</strong>
          </span>
        </div>
      </footer>
    </div>
  );
}
